import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Users, Gauge, Clock, Tag } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { Seo } from "@/components/site/Seo";
import { FloatingMotifs } from "@/components/site/Motifs";

const Info = ({ icon: Icon, label, value, testid }) =>
  value ? (
    <div className="flex items-start gap-3 rounded-2xl bg-bej-light p-4" data-testid={testid}>
      <Icon className="w-5 h-5 text-navy mt-0.5" />
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-navy/60">{label}</p>
        <p className="font-semibold text-navy">{value}</p>
      </div>
    </div>
  ) : null;

export default function CourseDetail() {
  const { slug } = useParams();
  const { data: c, error } = useFetch(`/courses/${slug}`);
  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-heading text-3xl font-black text-navy">Kurs bulunamadı</h1>
        <Link to="/kurslar" className="mt-6 inline-block font-bold text-navy underline">Kurslara dön</Link>
      </div>
    );
  }
  if (!c) return <div className="min-h-[50vh]" />;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Course", name: c.name, description: c.description,
    provider: { "@type": "EducationalOrganization", name: "Masal Sanat Akademi" },
  };
  return (
    <>
      <Seo title={c.name} description={`${c.name} kursu — ${c.age_group}. ${c.description}`.slice(0, 160)} jsonLd={jsonLd} />
      <section className="relative overflow-hidden bg-bej-light paper-grain">
        <FloatingMotifs />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link to="/kurslar" className="inline-flex items-center gap-2 text-sm font-bold text-navy/70 hover:text-navy" data-testid="course-back-link">
              <ArrowLeft className="w-4 h-4" /> Tüm kurslar
            </Link>
            <p className="mt-6 text-sm font-bold uppercase tracking-[0.2em] text-navy/60">{c.category_name}</p>
            <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-black text-navy" data-testid="course-title">{c.name}</h1>
            <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed whitespace-pre-line" data-testid="course-description">{c.description}</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              <Info icon={Users} label="Yaş grubu" value={c.age_group} testid="course-age" />
              <Info icon={Gauge} label="Seviye" value={c.level} testid="course-level" />
              <Info icon={Clock} label="Süre" value={c.duration} testid="course-duration" />
              {c.price != null && <Info icon={Tag} label="Ücret" value={`${c.price.toLocaleString("tr-TR")} ₺`} testid="course-price" />}
            </div>
            <BookButtonFor id={c.id} />
          </div>
          {c.image_url && (
            <div className="relative">
              <div className="absolute -inset-4 rounded-[3rem] bg-bej -rotate-2" aria-hidden="true" />
              <img src={c.image_url} alt={c.name} className="relative w-full aspect-[4/3] object-cover rounded-[2.5rem] shadow-xl" />
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function BookButtonFor({ id }) {
  return (
    <div className="mt-8">
      <Link
        to={`/randevu?kurs=${id}`}
        data-testid="course-book-btn"
        className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-white font-heading font-bold shadow-lg shadow-navy/20 transition-[background-color,transform] duration-200 hover:bg-navy-dark hover:-translate-y-0.5"
      >
        Bu kurs için randevu al
      </Link>
    </div>
  );
}
